<template>
  <div class="mb-5 overflow-y-visible">
    <div
      class="diagram-container position-relative"
      v-on:keydown.stop
    >
      <transition name="slide-in">
        <bpmn-side-panel
          v-show="showSidePanel"
          :workflow="workflow"
          :node="eventsById[latestEventId]"
          :types-to-display-in-diagram="requirementTypes"
          :cards="cardsByEventId[latestEventId]"
          :show-done="showDone"
          @add-card-ai="addCardAI"
          @ask-for-reply="onAskForReply"
          @update-requirement="onUpdateRequirement"
        />
      </transition>
      <template v-if="(!events || events?.length === 0) && !$store.state.isWorkflowDisabled && !creatingEvent">
        <div class="d-flex flex-row" :style="`position: absolute; z-index: 1; pointer-events: none; top: ${diagramYOffset}px;`">
          <v-btn
            variant="outlined"
            size="large"
            @click="addShape({parents: ['start']})"
            class="create-first-event-button ml-15 mt-14 mb-15"
            prepend-icon="mds:add-circle"
            style="pointer-events: auto;"
            :disabled="$store.state.isWorkflowDisabled"
          >
            Add start point
          </v-btn>
          <v-btn
            variant="outlined"
            size="large"
            class="generate-button ml-10 mt-14 mb-15 mr-5"
            @click="openAiGenerator()"
            prepend-icon="mds:temp_preferences_custom"
            style="pointer-events: auto;"
            :disabled="$store.state.isWorkflowDisabled"
          >
            Generate workflow with AI
          </v-btn>
        </div>
      </template>
      <div ref="canvas" class="canvas-container">
        <div ref="scrollContainer" class="scroll-container">
          <svg
            v-show="events"
            :width="diagramWidth"
            :height="flowChartHeight"
            id="flowchart"
            ref="flowchart"
          >
            <g v-for="(group) in svgGroups" :key="group.id">
              <foreignObject
                :key="group.id"
                :x="getXPointFromGroup(group)"
                :y="0"
                :width="group.width > 0 ? group.width : (diagramWidth - getXPointFromGroup(group))"
                height="30"
              >
                <div
                  style="position: relative;"
                  class="hover-container"
                  :data-test="group.name"
                >
                  <div
                    :class="{
                      'group-first': group.startSlotX === 0,
                      'group-last': group.width === -1
                    }"
                    class="group"
                    :id="group.id"
                    @keydown.stop="checkCharacterLimit($event, 20)"
                    @keydown.stop.enter="$event.target.blur()"
                    @keydown.stop.esc="restoreValue($event, group.name)"
                    @blur="updateGroupName($event, group)"
                    @paste="onPaste($event, $refs.groupName, 20, true)"
                    @click="closeInspectEvent()"
                    :contenteditable="!$store.state.isWorkflowDisabled"
                    v-text="group.name"
                  >
                  </div>
                  <span
                    v-if="!$store.state.isWorkflowDisabled"
                    class="delete-group-btn show-on-hover"
                    style="position: absolute; right: 10px; top: 5px;"
                  >
                    <v-icon
                      class="delete-group-icon"
                      size="x-small"
                      icon="mds:delete"
                      @click="deleteGroup(group)"
                    />
                  </span>
                </div>
              </foreignObject>
            </g>

            <svg
              :y="diagramYOffset"
              :width="diagramWidth"
              :height="flowChartHeight"
              id="svg"
            >
              <rect
                v-if="!hasGroups"
                :x="4"
                :y="0.5"
                :width="diagramWidth + 1"
                :height="flowChartHeight - 1"
                fill="none"
                stroke="#A3ADB9"
                class="main-rect"
              />
              <g
                v-for="(lane, name, index) in svgLanes"
                :key="'swimlane-' + lane.id"
                class="swime-lane-path"
                @click="closeInspectEvent()"
                @mouseenter="mouseEnterLane(lane)"
                @mouseleave="mouseLeaveLane($event, lane)"
              >
                <path
                  :d="
                    computeSwimlanePath(
                      lane.x + 24,
                      lane.y,
                      diagramWidth,
                      lane.height,
                      index === 0
                    )
                  "
                  style="stroke: #A3ADB9; stroke-width: 1px"
                ></path>
                <rect
                  :x="lane.x"
                  :y="lane.y"
                  :width="diagramWidth"
                  :height="lane.height"
                  :opacity="(index % 2 == 0) ? 0.0 : 0.01"
                  fill="black"
                  class="lane-rect"
                  :class="{
                    'cursor-grab': isGrabbable,
                  }"
                />
              </g>

              <template v-for="node in svgGraph">
                <!-- Pre Loop to print parts of the svg that has to be on the lower layer -->
                <!-- Event Path -->
                <template v-for="(parentId, indexParent) in node.parents" :key="node.id + indexParent + '80'">
                  <g v-if="parentId === 'start'">
                    <rect
                      :x="node.x - 26"
                      :y="node.y + 44"
                      width="8"
                      height="8"
                      fill="#1F2833"
                      rx="2"
                    />
                    <line
                      :x1="node.x - 26"
                      :y1="node.y + 48"
                      :x2="node.x + (node.type === 'bpmn:ExclusiveGateway' ? 4.5 : 0)"
                      :y2="node.y + 48"
                      stroke="#1F2833"
                      stroke-width="1"
                    />
                  </g>
                  <circle
                    v-if="svgGraph[parentId]"
                    :cx="svgGraph[parentId].x + (svgGraph[parentId].type === 'bpmn:ExclusiveGateway' ? 102 : 105)"
                    :cy="svgGraph[parentId].y + 96/2"
                    r="1" fill="#1F2833"
                  />
                  <path
                    v-if="svgGraph[parentId]"
                    :d="createPathBetweenNodes(parentId, node.id)"
                    class="sequence-arrow"
                    :class="{
                      highlighted: isArrowHighlighted(parentId, node.id),
                      outlined:
                        node.outlined || svgGraph[node.parents[0]]?.outlined,
                    }"
                  ></path>
                  <!-- labels forward connections -->
                  <template
                    v-if="
                      svgGraph[parentId] && connectionHasLabel(parentId, node.id)
                    "
                  >
                    <!-- editable Event Label -->
                    <foreignObject
                      :key="node.id + indexParent + 'event-label'"
                      :x="svgGraph[parentId].x + 140"
                      :y="node.y + 10"
                      width="120"
                      height="32"
                    >
                      <bpmn-event-label
                        v-if="!node.outlined"
                        :node="node"
                        :conditionLabel="getConnectionLabel(node, parentId)"
                        @label-blur="
                          blurOnConditionLabels($event, node, parentId)
                        "
                        @label-focus="focusOnConditionLabel"
                      ></bpmn-event-label>
                    </foreignObject>
                  </template>
                </template>
              </template>
              <template v-for="node in svgGraph" :key="node.id">
                <!-- Event -->
                <!-- Event Box -->
                <template v-if="node.type == 'bpmn:Task'">
                  <bpmn-event
                    :key="node.id + '40'"
                    :node="node"
                    :svgGraph="svgGraph"
                    :svgGroups="svgGroups"
                    :svgLanes="svgLanes"
                    :conditionLabel="(svgGroups || [])[node.groupId]?.name || 'null'"
                    :eventDefinition="workflow.eventDefinition"
                    :workflow="workflow"
                    :connection-target="
                      enableConnectionTargets &&
                        connectionTargetsIds.includes(node.id)
                    "
                    class="bpmn-event-container"
                    @move-right="$emit('move-right')"
                    @move-left="$emit('move-left')"
                  ></bpmn-event>
                </template>

                <template v-else>
                  <bpmn-gateway
                    :key="node.id + '60'"
                    :node="node"
                    :svgGraph="svgGraph"
                    :svgLanes="svgLanes"
                    :svgGroups="svgGroups"
                    :workflow="workflow"
                    :connection-target="
                      enableConnectionTargets &&
                        connectionTargetsIds.includes(node.id)
                    "
                    :eventDefinition="workflow.eventDefinition"
                    @move-right="$emit('move-right')"
                    @move-left="$emit('move-left')"
                  ></bpmn-gateway>
                </template>

                <!-- button menu on selected event or exclusive gateway -->
                <!--
                as for vue v2.6.11:
                Slots do not render in foreignObjects if the foreignObject
                is not in the template of the same component that has
                the root svg element of the svg. Means you can't split svg
                into components properly.

                https://github.com/vuejs/vue/issues/11315
                https://github.com/vuejs/vue/pull/11349

              must be fixed in vue v2.6.13
              -->

                <!-- Add event button -->
                <foreignObject
                  :x="node.x + (node.type === 'bpmn:ExclusiveGateway' ? 105 : 108)"
                  :y="node.y + 38"
                  width="40"
                  height="20"
                  class="add-event-button"
                  :class="{
                    'show-on-hover': $route.params.eventId !== node.id,
                  }"
                >
                  <v-btn
                    :disabled="$store.state.isWorkflowDisabled"
                    variant="flat"
                    size="16px"
                    class="captures-description"
                    :class="{
                      'opacity-100' : $route.params.eventId === node.id,
                    }"
                    @click="handleClickQuickAddEvent($event, node)"
                  >
                    <v-icon style="font-size: 16px;">mds:add</v-icon>
                  </v-btn>
                </foreignObject>

                <!-- colour badges on events and exclusive gateways -->
                <rect
                  v-if="node.type === 'bpmn:Task' || badgeDataByEvent[node.id]?.badges.length > 0"
                  :id="'rb-' + node.id"
                  :x="node.type === 'bpmn:Task' ? node.x : node.x + 28"
                  :y="node.y + 81"
                  :mask="`url(#rmask-${node.id})`"
                  fill="white"
                  opacity="0.3"
                  :width="node.type === 'bpmn:Task' ? 104 : 50"
                  height="15"
                />
                <foreignObject
                  :key="node.id + 'badge'"
                  v-if="requirementTypes?.length > 0"
                  :x="node.x"
                  :y="node.y + 81"
                  :mask="`url(#rmask-${node.id})`"
                  width="104"
                  height="16"
                >
                  <div :class="`d-flex ${node.type === 'bpmn:ExclusiveGateway' ? 'justify-center' : 'justify-left'} align-left`">
                    <div
                      v-for="(badge, index) in (badgeDataByEvent[node.id]?.badges.slice(0, badgeDataByEvent[node.id]?.badges.length > 6 ? 5 : 6) || [])"
                      @click="inspectEvent(node.id)"
                      :key="badge.id"
                      :class="{
                        'requirement-badge': true,
                        'd-none': badge.count <= 0,
                        'ml-05': index !== 0,
                        'flex-grow-1': badgeDataByEvent[node.id]?.badges.length === 6
                      }"
                      :style="'background-color:' + getBackgroundColour(badge)"
                    >
                      <span
                        class="requirement-badge-number"
                        :style="'color: ' + getTextColour(badge)"
                      >
                        {{ badge.count > 99 ? `${badge.count.toString()[0]}..` : badge.count }}
                      </span>
                    </div>

                    <div
                      v-if="badgeDataByEvent[node.id]?.badges.length === 0 && node.type === 'bpmn:Task'"
                      style="background-color: white;" class="requirement-badge"
                      @click="inspectEvent(node.id)"
                    >
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <span v-bind="props" class="requirement-badge-number">+</span>
                        </template>
                        <span>Add cards using the sidebar</span>
                      </v-tooltip>
                    </div>

                    <div
                      v-if="!!badgeDataByEvent[node.id] && badgeDataByEvent[node.id].badges.length > 6"
                      class="requirement-badge requirement-more-badge"
                      @click="inspectEvent(node.id)"
                    >
                      <span class="requirement-badge-number">
                        +{{
                          badgeDataByEvent[node.id].restCardsCount > 99 ?
                            `${badgeDataByEvent[node.id].restCardsCount.toString()[0]}..` :
                            badgeDataByEvent[node.id].restCardsCount
                        }}
                      </span>
                    </div>
                  </div>
                </foreignObject>
              </template>

              <!-- definitions of arrow -->
              <defs>
                <marker
                  id="sequenceflow-end-white-black-1r79xjtcycpwpzz6dais2xasb"
                  viewBox="0 0 40 40"
                  refX="17"
                  refY="23"
                  markerWidth="40"
                  markerHeight="40"
                  orient="0"
                >
                  <path
                    d="M 22.208 22.226 C 22.807 22.622 22.807 23.5 22.208 23.896 L 19.773 25.502 C 19.108 25.941 18.223 25.464 18.223 24.667 L 18.223 21.455 C 18.223 20.658 19.108 20.181 19.773 20.62 L 22.208 22.226 Z"
                    style="stroke-width: 0 !important;"
                    fill="#1F2833"
                  />
                </marker>
              </defs>

              <g v-for="group of Object.values(svgGroups).slice(1)" :key="group.id">
                <path
                  :d="`M ${group.x} 0 V 999999`"
                  style="stroke: rgb(170, 170, 170); stroke-width: 1px;"
                ></path>
                <path
                  v-show="dragging && group.id === selectedElement?.id"
                  :d="`M ${groupDragX} 0 V 999999`"
                  style="stroke: rgb(170, 170, 170); stroke-width: 2px;"
                ></path>
                <rect
                  :data-test="`group-divider-${group.name}`"
                  :id="group.id"
                  :x="group.x - 2" y="0"
                  width="3" height="999999"
                  :class="group.startSlotX === 0 ? 'notDraggable' : 'draggable horizontal'"
                  :fill="dragging && group.id === selectedElement?.id ? 'rgb(46, 156, 230)' : 'transparent'"
                  stroke="transparent"
                >
                </rect>
              </g>
            </svg>
          </svg>

          <transition name="actions">
            <div v-if="showActions">
              <div
                v-if="
                  $route.params.eventId &&
                    svgGraph?.[$route.params.eventId] &&
                    !$store.state.isWorkflowDisabled
                "
                :style="`
                position: absolute;
                left: ${getActionsMenuXPosition(svgGraph[$route.params.eventId])}px;
                top: ${constrainActionsMenuYPosition(svgGraph[$route.params.eventId])}px;
                transition: left ease 0.15s, top ease 0.15s;
                height: 21;
              `"
                class="text-left"
              >
                <bpmn-diagram-actions
                  :selectedEvent="svgGraph[$route.params.eventId]"
                  :enableConnectionTargets="enableConnectionTargets"
                  :events="events"
                  :baseData="baseData"
                  :svgGraph="svgGraph"
                  :svgGroups="svgGroups"
                  :svgLanes="svgLanes"
                  :workflow="workflow"
                  :color="
                    svgGraph[$route.params.eventId].color
                      ? svgGraph[$route.params.eventId].color.rgb
                      : workflow.eventDefinition.color.rgb
                  "
                  @change-shape="showTransformOptions = !showTransformOptions"
                  @add-shape="addShape"
                  @add-shape-ai="addShapeAI"
                  @add-group="addGroup"
                />
              </div>
            </div>
          </transition>

          <!-- Shape Right is moved into BpmnDiagramActions above
          <div
            v-if="
              $route.params.eventId &&
              svgGraph?.[$route.params.eventId] &&
              !$store.state.isWorkflowDisabled
            "
            v-show="!dragging"
            :style="`
            position: absolute;
            left: ${svgGraph?.[$route.params.eventId]?.x + 105}px;
            top: ${svgGraph?.[$route.params.eventId]?.y - 1 + diagramYOffset}px;
            width: 22;
            height: 105;
          `"
          >
            <bpmn-diagram-actions-shape-right
              v-if="svgGraph[$route.params.eventId]"
              :selectedEvent="svgGraph[$route.params.eventId]"
              :baseData="baseData"
              @add-shape="addShape"
              :svgGraph="svgGraph"
              :svgGroups="svgGroups"
              :svgLanes="svgLanes"
            ></bpmn-diagram-actions-shape-right>
          </div>
          -->

          <!-- Subprocess icon -->
          <div
            v-for="node in subprocesses"
            :key="node.id + '-subprocess'"
            width="14"
            height="14"
            style="line-height: 1em; font-size: 0"
            class="subprocess-icon"
            :style="`
            position: absolute;
            left: ${svgGraph?.[node.id]?.x + 43}px;
            top: ${svgGraph?.[node.id]?.y + 62 + diagramYOffset}px;
            width: 14px;
            height: 14px;
          `"
          >
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ on, props }">
                <router-link
                  :to="
                    '/workflow/' +
                      $route.params.projectId +
                      '/' +
                      node.subprocess.id
                  "
                  target="_blank"
                >
                  <v-icon v-bind="props" size="14" v-on="{ ...on }">custom:SubprocessIcon</v-icon>
                </router-link>
              </template>
              <span>Open subprocess: {{ node.subprocess.name }}</span>
            </v-tooltip>
          </div>
        </div>

        <div
          class="context-menu-top"
          v-if="svgGraph?.[$route.params.eventId]?.x && showTransformOptions"
          :style="`
            position: absolute;
            left: ${
            svgGraph?.[$route.params.eventId]?.x +
            56 -
            bpmnScrollLeft
          }px;
            top: ${svgGraph?.[$route.params.eventId]?.y - 48 - scrollTop + diagramYOffset}px;
            width: 70;
            height: 21;
          `"
          v-show="!dragging"
        >
          <bpmn-diagram-actions-shape-top-transform
            class="dark position-fixed"
            :selectedEvent="svgGraph[$route.params.eventId]"
            :baseData="baseData"
            :svgGraph="svgGraph"
            :svgGroups="svgGroups"
            :svgLanes="svgLanes"
            @transform-shape="hideTransformOptions"
            v-click-outside="closeTransformOptions()"
          />
        </div>
        <!-- Swimlane titles -->
        <div
          v-for="(lane, name, index) in svgLanes"
          :key="index"
          :style="`
            width: 30px;
            position: absolute;
            left: ${lane.x}px;
            top: ${lane.y + diagramYOffset}px;
            height: ${lane.height}px;
          `"
        >
          <!-- editable Lane name (rotated) -->
          <div class="lane-title-container position-relative" style="z-index: 12;">
            <bpmn-swim-lane-title
              :first="index === 0 && !hasGroups"
              :last="index === noOfLanes - 1"
              :lane="lane"
              :index="index"
              :color="workflow.roleDefinition.color.rgb"
              :svgGraph="svgGraph"
              :svgLanes="svgLanes"
              :svgGroups="svgGroups"
              :workflow="workflow"
              @name-blur="blurOnRoleName"
            ></bpmn-swim-lane-title>

            <v-tooltip location="top" v-if="!$store.state.isWorkflowDisabled" open-delay="500">
              <template v-slot:activator="{ on, props }">
                <div
                  :ref="`delete-role-button-${workflow.lanes?.[index]?.id}`"
                  class="delete-role-button"
                  @click="
                    deleteLane(
                      workflow.lanes[index] && workflow.lanes[index].id,
                      index
                    )
                  "
                >
                  <button class="captures-description">
                    <v-icon v-bind="props" class="delete-lane-icon" v-on="{ ...on }"
                    >mdi-delete-outline</v-icon
                    >
                  </button>
                </div>
              </template>
              <span>Delete this role</span>
            </v-tooltip>
          </div>
        </div>
      </div>

      <bpmn-diagram-actions-bottom
        :selectedEvent="svgGraphData[$route.params.eventId]"
        :requirement-types="requirementTypes"
        :base-data="baseData"
        :svgGraph="svgGraph"
        :svgGroups="svgGroups"
        :svgLanes="svgLanes"
        @add-shape="addShape"
        @add-shape-ai="addShapeAI"
        @add-group="addGroup"
      ></bpmn-diagram-actions-bottom>
    </div>

    <bpmn-generator
      :bpm="thisComponent()"
      :workflow="workflow"
      :workflowTasks="events ? events : {}"
      ref="generator"
    />
  </div>
</template>

<script>
import BpmnEvent from '@/components/BpmnEvent'
import BpmnGateway from '@/components/BpmnGateway'
import BpmnDiagramActions from '@/components/BpmnDiagramActions'
import BpmnDiagramActionsBottom from '@/components/BpmnDiagramActionsBottom'
import BpmnSwimLaneTitle from '@/components/BpmnSwimLaneTitle'
import BpmnSidePanel from '@/components/BpmnSidePanel'
import BpmnDiagramActionsShapeTopTransform from '@/components/BpmnDiagramActionsShapeTopTransform'
import BpmnEventLabel from '@/components/BpmnEventLabel'
// import BpmnEventLabelVertical from '@/components/BpmnEventLabelVertical'
import workflowApi from '@/api/workflowApi'
import { workflowApiHelpers } from '@/api/workflowApiHelpers'
import requirementTypesApi from '@/api/requirementTypesApi'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import { EventBus } from '@/event-bus/event-bus.js'
import BpmnGenerator from '@/components/BpmnGenerator'
import { mapState, mapGetters, mapMutations } from 'vuex'
import randomUUID from '@/utils/uuid'
import { scrollToEvent } from '@/utils/scroll'
import safeStructuredClone from '@/utils/clone'
import dragFunctions from '@/helpers/dragFunctions'
import positionHelpers from '@/helpers/positionHelpers'
import dataHelpers from '@/helpers/dataHelpers'
import svgPathHelpers from '@/helpers/svgPathHelpers'

export default {
  name: 'Bpmn',
  components: {
    BpmnEvent,
    BpmnEventLabel,
    BpmnGateway,
    BpmnDiagramActions,
    BpmnDiagramActionsBottom,
    BpmnSwimLaneTitle,
    BpmnSidePanel,
    BpmnDiagramActionsShapeTopTransform,
    BpmnGenerator
  },
  data () {
    return {
      aiGenerator: false,
      selectedElement: null,
      timeout: null,
      dragOffsetX: null,
      dragOffsetY: null,
      startDragX: null,
      startDragY: null,
      slotX: 0,
      watchInPause: false,
      svgGraph: null,
      svgLanes: null,
      cardsByEventId: {},
      svgGroups: {},
      // savingChanges: false,
      dragging: false,
      showActions: false,
      latestEventId: null,
      enableConnectionTargets: false,
      showTransformOptions: false,
      triggerUpdateFromDrag: false,
      autoscroll: null,
      clientY: null,
      scrollTop: null,
      scrollLeft: null,
      occupiedSlotsVerticalLines: {},
      creatingEvent: false,
      draggingCanvasStartX: 0,
      isDraggingCanvas: false
    }
  },
  props: [
    'showDone'
  ],
  mixins: [diagramHelpers],
  computed: {
    ...mapGetters({
      baseData: 'svgGraphData/baseData',
      svgLanesData: 'svgGraphData/svgLanesData',
      svgGraphData: 'svgGraphData/svgGraphData',
      svgGroupsData: 'svgGraphData/svgGroupsData',
      diagramWidth: 'synchronizedScrolling/diagramWidth',
      loadingWorkflowUpdate: 'workflow/getLoadingUpdate'
    }),
    ...mapState({
      workflow: state => state.workflow.workflow,
      requirementTypes: state => state.requirementTypes.requirementTypes,
      events: (state) => state.workflow.workflow.eventsJson || [],
      showReleaseNo: state => state.workflow.showReleaseNo,
      bpmnScrollLeft: state => state.synchronizedScrolling.bpmnScrollLeft,
      sidePanelTab: state => state.sidePanelTab,
      showSidePanel: state => state.showSidePanel,
      hasClosedSidePanel: state => state.hasClosedSidePanel,
      svgContainerWidth: state => state.synchronizedScrolling.svgContainerWidth
    }),
    hasGroups () {
      return Object.keys(this.svgGroups)?.length > 0
    },
    diagramYOffset () {
      return this.hasGroups ? 24 : 0
    },
    flowChartHeight () {
      let res = this.diagramYOffset
      for (const lane in this.svgLanes) {
        res = res + this.svgLanes[lane].height
      }
      if (Object.values(this.svgLanes || {}).length === 0) res = 156
      return res
    },
    noOfLanes () {
      return Object.keys(this.svgLanes).length
    },
    eventsById () {
      if (!this.events) { return {} }
      return this.events.reduce((accum, curr) => {
        accum[curr.id] = curr
        return accum
      }, {})
    },
    listValidTargetsForSequenceArrows () {
      const { eventId } = this.$route.params

      if (!eventId || !Array.isArray(this.events)) {
        return []
      }

      const res = this.events.filter(wt =>
        (wt.id !== eventId) &&
        (!wt.parents || !wt.parents.find(parent => parent === eventId))
      )
      return res
    },
    connectionTargetsIds () {
      return this.listValidTargetsForSequenceArrows.map(({ id }) => id)
    },
    subprocesses () {
      return this.events ? this.events.filter(event => event.subprocess) : []
    },
    groupDragX () {
      return positionHelpers.getGroupXPointFromSlot(this.slotX)
    },
    slotToGroupMap () {
      const groups = Object.values(this.svgGroups)
      if (groups?.length > 0) {
        return dataHelpers.slotToGroupMap(groups, this.baseData.columns.length)
      } else {
        return {}
      }
    },
    badgeDataByEvent () {
      const badgeByEvent = {}
      Object.values(this.svgGraph).forEach(node => {
        badgeByEvent[node.id] = this.computeEventBadgesData(node)
      })
      return badgeByEvent
    },
    isGrabbable () {
      return this.svgContainerWidth < this.diagramWidth
    }
  },
  methods: {
    ...mapMutations({
      setBpmnScrollLeft: 'synchronizedScrolling/setBpmnScrollLeft',
      setSvgContainerWidth: 'synchronizedScrolling/setSvgContainerWidth'
    }),
    handleClickQuickAddEvent (event, node) {
      // blur the button to avoid focus issues
      document.activeElement.blur()
      if (this.$store.state.isWorkflowDisabled) {
        return
      }
      this.addShape({ parents: [node.id] }, { parentEventId: node.id })
    },
    isArrowHighlighted (parentId, childId) {
      if (this.dragging) {
        return false
      }
      let xSlot
      const parentNode = this.svgGraph[parentId]
      const childNode = this.svgGraph[childId]
      // only highlight arrows around the selected event
      if ([parentId, childId].includes(this.$route.params.eventId)) {
        if (parentNode.type === 'bpmn:ExclusiveGateway') {
          // the vertical line after a decision
          // is positioned directly after the desicion
          xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
        } else {
          // the vertical line after an event
          // is positioned directly before the child
          xSlot = positionHelpers.getSlotXFromPoint(childNode.x) - 1
        }
        let ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
        const ySlotEnd = positionHelpers.getSlotYFromPoint(childNode.y)
        const backwardsConnection = parentNode.x > childNode.x
        if (backwardsConnection) {
          // if the parent is above the child
          // the vertical line still approaches the child from one step below
          if (ySlotEnd > ySlotStart) {
            ySlotStart = ySlotEnd + 1
          }
          if (this.isColliding(xSlot, ySlotStart, ySlotEnd, backwardsConnection)) {
            return true
          }

          // the parent has a downwards arrow that also needs to be
          // checked for collisions
          xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
          ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
          if (this.isColliding(xSlot, ySlotStart, ySlotStart + 1, backwardsConnection)) {
            return true
          }
        } else {
          // forward connection
          if (ySlotStart !== ySlotEnd) {
            if (this.isColliding(xSlot, ySlotStart, ySlotEnd, backwardsConnection)) {
              return true
            }
          }
        }
      }
      return false
    },
    getXPointFromGroup (group) {
      return positionHelpers.getGroupXPointFromSlot(group.startSlotX)
    },
    inspectEvent (eventId, tab = 'cards') {
      this.changeSelectedEvent(eventId)
      this.latestEventId = eventId
      this.$store.commit('setSidePanelTab', tab)
      this.$store.commit('showSidePanel', true)
    },
    closeInspectEvent () {
      this.changeSelectedEvent(null)
      this.latestEventId = null
    },
    openAiGenerator () {
      this.$refs.generator.openSettings()
    },
    onAskForReply (event) {
      this.$refs.generator.addReplyToDiagram(event)
    },
    onUpdateRequirement (requirement) {
      workflowApi.updateRequirementsJsonMutation(this.$route.params.id, [{
        requirementId: requirement.id,
        sortkey: requirement.sortkey,
        releaseNo: requirement.releaseNo,
        cardTypeId: requirement.cardTypeId,
        updateNote: {
          action: 'moved card',
          target: requirement.description
        }
      }])
    },
    thisComponent () {
      return this
    },
    mouseEnterLane (lane) {
      if (lane.emptyLane) {
        if (this.$refs[`delete-role-button-${lane.id}`] && this.$refs[`delete-role-button-${lane.id}`][0]) {
          this.$refs[`delete-role-button-${lane.id}`][0].style.display = 'block'
        }
      }
    },
    mouseLeaveLane (event, lane) {
      if (!(event.relatedTarget?.classList?.contains('delete-lane-icon'))) {
        if (this.$refs[`delete-role-button-${lane.id}`] && this.$refs[`delete-role-button-${lane.id}`][0]) {
          this.$refs[`delete-role-button-${lane.id}`][0].style.display = 'none'
        }
      }
    },
    resetDiagram () {
      this.endDrag(false)
      this.svgGraph = JSON.parse(JSON.stringify(this.svgGraphData))
      this.svgLanes = JSON.parse(JSON.stringify(this.svgLanesData))
      this.svgGroups = JSON.parse(JSON.stringify(this.svgGroupsData))

      this.$store.commit('updateSvgGraph', JSON.parse(JSON.stringify(this.svgGraphData)))
      this.$store.commit('updateSvgLanes', JSON.parse(JSON.stringify(this.svgLanesData)))
    },
    computeEventBadgesData (node) {
      const badges = []

      for (const cardType of this.requirementTypes) {
        const cardsToShow = node.requirementsJson?.filter((req) => {
          return !req.done &&
            req.cardType &&
            req.cardType.id === cardType.id &&
            (!this.showReleaseNo || this.showReleaseNo?.length === 0 || this.showReleaseNo?.includes(req.releaseNo))
        })

        const count = cardsToShow?.length || 0

        if (count > 0) {
          badges.push({ ...cardType, count })
        }
      }

      return { badges, restCardsCount: badges.slice(5).reduce((a, b) => a + b.count, 0) }
    },
    computeCardsByEventId () {
      if (!this.events) { return {} }
      return this.events.reduce((accum, event) => {
        accum[event.id] = event.requirementsJson?.filter((req) =>
          !this.showReleaseNo ||
          this.showReleaseNo.length === 0 ||
          this.showReleaseNo.includes(req.releaseNo)
        ) || []
        return accum
      }, {})
    },
    delay (callback) {
      this.timeout = setTimeout(callback, 500)
    },
    resetDelay () {
      clearTimeout(this.timeout)
    },
    getBackgroundColour (cardType) {
      return requirementTypesApi.getBackgroundColour(cardType)
    },
    getTextColour (cardType) {
      return requirementTypesApi.getTextColour(cardType)
    },
    connectionHasLabel (parentId, childId) {
      const xDistance = this.svgGraph[childId].x - this.svgGraph[parentId].x
      if (xDistance > 160) {
        if (this.svgGraph[parentId].type === 'bpmn:ExclusiveGateway') {
          return true
        }
      }
      return false
    },
    createPathBetweenNodes (parentId, childId) {
      const parentNode = this.svgGraph[parentId]
      const childNode = this.svgGraph[childId]
      const xDistance = childNode.x - parentNode.x
      const coordinates = svgPathHelpers.getConnectionCoordinates(parentNode, childNode)
      this.handleMarkOccupied(parentNode, childNode, coordinates)
      const breakingPoint = svgPathHelpers.getBreakingPoint(parentNode.type, childNode.type)
      if (xDistance > breakingPoint) {
        return svgPathHelpers.createForwardConnection(parentNode.type, coordinates)
      } else if (xDistance < 0) {
        const childIsStartNode = childNode.parents.find(it => it === 'start')
        return svgPathHelpers.createBackwardConnection(childIsStartNode, coordinates)
      } else {
        return svgPathHelpers.createVerticalLine(parentId, childId, coordinates)
      }
    },
    handleMarkOccupied (parentNode, childNode, { xStart, yStart, xEnd, yEnd }) {
      if (!this.dragging && (yStart !== yEnd || xEnd < xStart)) {
        let xSlot
        if (parentNode.type === 'bpmn:ExclusiveGateway') {
          xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
        } else {
          xSlot = positionHelpers.getSlotXFromPoint(childNode.x) - 1
        }
        let ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
        if (xEnd < xStart) {
          ySlotStart = ySlotStart + 1
        }
        const ySlotEnd = positionHelpers.getSlotYFromPoint(childNode.y)
        if (this.$route.params.eventId) {
          if (childNode.id !== this.$route.params.eventId && parentNode.id !== this.$route.params.eventId) {
            this.markOccupied(xSlot, ySlotStart, ySlotEnd)
            if (xEnd < xStart) {
              xSlot = positionHelpers.getSlotXFromPoint(parentNode.x)
              ySlotStart = positionHelpers.getSlotYFromPoint(parentNode.y)
              this.markOccupied(xSlot, ySlotStart, ySlotEnd + 1)
            }
          }
        }
      }
    },
    addRole () {
      // console.log(this.workflow, this.workflow.lanes)
      const roleName = 'Lane ' + ((this.workflow.lanes?.length || 0) + 1)
      this.addLane(this.svgGraph, this.svgLanes, this.svgGroups, this.workflow, roleName, true)
    },
    async addShapeAI (type, parentId = 'start', description = null, laneName = null) {
      this.$refs.generator.addShapeToDiagram(parentId)
    },
    async addCardAI (eventId, cardTypeId) {
      this.$refs.generator.addCardToDiagram(eventId, cardTypeId)
    },
    async addShape (shape = {}, options = {}) {
      this.creatingEvent = true
      // use nextTick to wait for blur events to complete first to update descriptions or labels
      await this.$nextTick()
      const selectedEventId = this.$route.params.eventId && this.svgGraph[this.$route.params.eventId]?.id
      const parentId = options.parentEventId || selectedEventId || 'start'
      if (parentId !== 'start' && this.workflow?.eventsJson?.findIndex(event => event.id === parentId) === -1) {
        // prevent double click on the same event, not necessary check but makes frontend nicer
        return
      }
      shape.parents = [parentId]
      const newEventId = await workflowApi.addEventToWorkflow(
        shape,
        options,
        this.svgLanes,
        this.svgGraph,
        this.svgGroups,
        this.workflow
      )
      this.$router.push({ path: '/workflow/' + this.workflow.projectId + '/' + this.workflow.id + '/' + newEventId }).catch(() => {}).finally(() => {
        this.creatingEvent = false
      })
      scrollToEvent(newEventId)
    },
    addGroup (anchorEventId) {
      let startSlotX = 0
      const groupArray = Object.values(this.svgGroups)
      if (this.hasGroups) {
        const columnCount = this.baseData.columns.length
        const lengthArray = groupArray.map(group => group.startSlotX)
        const lastGroup = Math.max(...lengthArray)
        startSlotX = Math.max(lastGroup + 1, columnCount)
      }

      const group = {
        id: randomUUID(),
        name: 'Group ' + (groupArray.length + 1),
        startSlotX,
        x: positionHelpers.getGroupXPointFromSlot(startSlotX),
        width: -1
      }
      this.svgGroups[group.id] = group

      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'added group',
          target: group.name
        }
      })
    },
    updateGroupName (event, group) {
      const { textContent: newValue } = event.target
      this.svgGroups[group.id].name = newValue
      if (event.relatedTarget?.classList?.contains('captures-description')) {
        // anoter button was pushed that will
        // handle the update of the group name
        return
      }
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'changed name of group',
          target: group.name
        }
      }, true)
    },
    deleteGroup (group) {
      workflowApi.deleteGroup(group, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
    },
    deleteLane (id) {
      const name = this.svgLanes[id].name
      // update the height of the lane above the last lane
      // if the last lane is deleted
      if (!this.svgLanes[id].laneBelow) {
        const laneAbove = this.svgLanes[id].laneAbove
        if (laneAbove) {
          this.svgLanes[laneAbove].height += 15
        }
      }
      if (this.$refs[`delete-role-button-${id}`] && this.$refs[`delete-role-button-${id}`][0]) {
        this.$refs[`delete-role-button-${id}`][0].style.display = 'none'
      }
      delete this.svgLanes[id]
      workflowApi.updateWorkflow({
        svgLanes: this.svgLanes,
        svgGraph: this.svgGraph,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'deleted lane',
          target: name
        }
      })
    },
    enableAutoscroll (event) {
      this.clientY = event.clientY // save starting pos
      this.autoscroll = setInterval(() => {
        if (this.dragging) {
          if (this.clientY < 93 && this.selectedElement.y > 0) {
            // autoscroll up
            window.scrollBy(0, -5)
            this.scrollTop -= 5
            this.selectedElement.y -= 5
          } else {
            // autoscroll down
            const boundingRect = document.getElementById('flowchart')?.getBoundingClientRect()
            if (boundingRect) {
              const bottomOfSvg = boundingRect.y + boundingRect.height
              if ((window.innerHeight - this.clientY) < 93 && (bottomOfSvg - this.clientY) > 30) {
                window.scrollBy(0, 5)
                this.scrollTop += 5
                this.selectedElement.y += 5
              }
            }
          }
        }
      }, 20)
    },
    disableAutoscroll () {
      clearInterval(this.autoscroll)
    },
    updateAutoscrollParameters (event) {
      this.clientY = event.clientY
    },
    startDrag (event) {
      // dragging canvas left and right
      if (event.target.classList.contains('lane-rect')) {
        this.isDraggingCanvas = true
        this.draggingCanvasStartX = event.clientX
        return
      }
      if (!this.selectedElement && !this.$store.state.isWorkflowDisabled) {
        const targetId = event.target.id.replace('p-', '')
        const isDraggable = event.target.classList.contains('draggable')
        if (isDraggable) {
          if (this.svgGraph[targetId]) {
            this.selectedElement = this.svgGraph[targetId]
            document.querySelectorAll(`[id='${this.selectedElement.id}']`).forEach(el => {
              el.removeAttribute('data-test-slot')
            })
          }

          if (this.svgGroups[targetId]) {
            this.selectedElement = this.svgGroups[targetId]
          }

          if (this.selectedElement) {
            this.dragging = true
            this.dragOffsetX = event.pageX - this.selectedElement.x
            this.dragOffsetY = event.pageY - this.selectedElement.y
            this.startDragX = event.pageX
            this.startDragY = event.pageY
            this.watchInPause = true
            this.slotX = 0
            if (this.svgGraph[this.selectedElement.id]) {
              this.slotX = positionHelpers.getSlotXFromId(this.selectedElement.id, this.svgGraph)
            } else if (this.svgGroups[this.selectedElement.id]) {
              this.slotX = this.svgGroups[this.selectedElement.id].startSlotX
            }
            this.triggerUpdateFromDrag = false
            this.currentScrollLeft = this.$refs.scrollContainer.scrollLeft
            this.disableNormalScroll()
            this.enableAutoscroll(event)
          }
        }
      }
    },
    endDrag (update = true) {
      this.disableAutoscroll()
      this.enableNormalScroll()
      if (this.selectedElement) {
        if (this.svgGraph[this.selectedElement.id]) {
          // dragging a shape
          this.snapToGrid(this.selectedElement)
          const yMovement = Math.abs(this.dragOffsetY - this.startDragY + this.selectedElement.y)
          document.querySelectorAll(`[id='${this.selectedElement.id}']`).forEach(el => {
            el.removeAttribute('data-test-slot')
          })

          const svgGraphClone = safeStructuredClone(this.svgGraph)

          if (this.showReleaseNo) {
            // In case if we have applied filter by release number (showReleaseNo), the requirementsJson in the
            // svgGraphData will be different from original value and will trigger unnecessary update, so we need
            // to set it undefined to prevent that

            Object.keys(svgGraphClone).forEach((key) => {
              svgGraphClone[key].requirementsJson = undefined
            })
          }

          if (update && (this.triggerUpdateFromDrag || yMovement > 0)) {
            workflowApi.updateWorkflow({
              svgGraph: svgGraphClone,
              svgLanes: this.svgLanes,
              svgGroups: this.svgGroups,
              workflow: this.workflow,
              updateNote: {
                action: this.selectedElement.type === 'bpmn:ExclusiveGateway' ? 'moved decision' : 'moved event',
                target: this.selectedElement.name
              }
            })
          }
        }

        if (this.svgGroups[this.selectedElement.id]) {
          // dragging a group divider
          this.svgGroups[this.selectedElement.id].startSlotX = this.slotX
          this.selectedElement.x = positionHelpers.getGroupXPointFromSlot(this.slotX)
          workflowApi.updateGroupStartingPosition(this.selectedElement.id, this.svgGraph, this.svgLanes, this.svgGroups, this.workflow)
        }
      }
      this.selectedElement = null
      this.dragging = false
      this.dragOffsetX = null
      this.dragOffsetY = null
      this.lastComittedSlotX = null
      this.triggerUpdateFromDrag = false
      this.currentScrollLeft = 0
      this.isDraggingCanvas = false
      this.DraggingCanvasStartX = 0
    },
    drag (event) {
      this.clientY = event.offsetY
      this.screenY = event.screenY
      if (this.selectedElement) {
        this.updateAutoscrollParameters(event)
        this.watchInPause = false
        if (this.svgGraph[this.selectedElement.id]) {
          // Dragging an event
          this.dragging = true
          this.selectedElement.x = event.pageX - this.dragOffsetX + (this.$refs.scrollContainer.scrollLeft - this.currentScrollLeft)
          this.selectedElement.y = event.pageY - this.dragOffsetY
          const { slotX } = positionHelpers.getSlotsFromNode(this.selectedElement, this.svgGraph)
          if (
            (
              slotX === (this.slotX + 1) &&
              (
                this.hasSlotToTheRight(this.selectedElement) ||
                !this.selectedElement.childIds?.length > 0 ||
                this.hasGroups
              )
            ) ||
            (
              slotX === (this.slotX - 1) &&
              this.hasSlotToTheLeft(this.selectedElement)
            )
          ) {
            this.slotX = slotX
            this.triggerUpdateFromDrag = true // changed slot
          }
        } else if (this.svgGroups[this.selectedElement.id]) {
          // Dragging a group
          const previousGroup = this.workflow.groups[this.selectedElement.index - 1]
          const maxLeft = 50 + (previousGroup.startSlotX + 1) * 160
          const nextGroup = this.workflow.groups[this.selectedElement.index + 1]
          const maxRight = 50 + (nextGroup ? (nextGroup.startSlotX - 1) : 9999) * 160
          const pointX = event.pageX - this.dragOffsetX + (this.$refs.scrollContainer.scrollLeft - this.currentScrollLeft)
          this.selectedElement.x = Math.min(Math.max(maxLeft, pointX), maxRight)
          this.selectedElement.y = event.pageY - this.dragOffsetY
          const slotX = positionHelpers.getSlotXFromPoint(this.selectedElement.x)
          if (slotX !== this.slotX) {
            this.slotX = slotX
            this.triggerUpdateFromDrag = true // changed slot
            this.svgGroups[this.selectedElement.id].startSlotX = this.slotX
            this.svgGroups[this.selectedElement.id].width = nextGroup ? (nextGroup.startSlotX - this.slotX) * 160 : -1
            this.svgGroups[previousGroup.id].width = (this.slotX - previousGroup.startSlotX) * 160 + (previousGroup.startSlotX === 0 ? 50 : 0)
          }
        }
      }
      if (this.isDraggingCanvas) {
        this.$refs.scrollContainer.scrollLeft += this.draggingCanvasStartX - event.clientX
        this.draggingCanvasStartX = event.clientX
      }
    },
    getYPointFromSlot (slot) {
      return 30 + 140 * slot
    },
    hasSlotToTheLeft (selectedElement) {
      const isStartingPoint = (selectedElement.parents || []).findIndex(id => id === 'start') >= 0
      if (!isStartingPoint &&
        selectedElement.parents.findIndex(parentId => positionHelpers.getSlotXFromId(parentId, this.svgGraph) <= this.slotX) >= 0) {
        return true
      } else if (selectedElement.groupId && this.svgGroups[selectedElement.groupId].slotX !== 0) {
        return true
      } else {
        return false
      }
    },
    hasSlotToTheRight (selectedElement) {
      if (selectedElement.childIds?.length > 0 &&
        selectedElement.childIds.findIndex(childId => positionHelpers.getSlotXFromId(childId, this.svgGraph) >= this.slotX) >= 0) {
        return true
      } else {
        return false
      }
    },
    snapToGrid (draggedNode) {
      let lane
      // snap to grid x
      const isStartingPoint = (draggedNode.parents || []).findIndex(id => id === 'start') >= 0
      if (draggedNode.groupId && this.svgGroups[draggedNode.groupId]) {
        draggedNode.x = 80 + this.svgGroups[draggedNode.groupId].startSlotX * 160
      } else if (draggedNode.parents && draggedNode.parents[0] && !isStartingPoint) {
        draggedNode.x = this.svgGraph[draggedNode.parents[0]].x + 160
      } else if (isStartingPoint) {
        draggedNode.x = 80
      } else {
        if (draggedNode.childIds && draggedNode.childIds[0]) {
          draggedNode.x = this.svgGraph[draggedNode.childIds[0]].x - 160
        }
      }

      // find lane where dropped
      for (lane in this.svgLanes) {
        if ((draggedNode.y + 40) < (this.svgLanes[lane].y + this.svgLanes[lane].height)) {
          break
        }
      }
      // snap to grid y
      draggedNode.offset = Math.floor((draggedNode.y + 40 - this.svgLanes[lane].y) / 156)
      // prevent dropping outside lane
      if (draggedNode.offset < 0) {
        draggedNode.offset = 0
      }
      const laneHeightInOffsetUnits = (this.svgLanes[lane].height / 156) - 1
      if (draggedNode.offset > laneHeightInOffsetUnits) {
        draggedNode.offset = laneHeightInOffsetUnits
      }

      draggedNode.y = this.svgLanes[lane].y + 30 + draggedNode.offset * 156
      draggedNode.laneId = lane
      this.svgGraph[draggedNode.id] = draggedNode
      workflowApiHelpers.updateVerticalPositionsIfCollision(this.svgGraph, this.svgLanes, draggedNode.id, true)
    },
    disableNormalScroll () {
      // Get the current page scroll position
      this.scrollTop = window.pageYOffset || document.documentElement.scrollTop
      this.scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

      // if any scroll is attempted, set this to the previous value
      window.onscroll = () => {
        window.scrollTo(this.scrollLeft, this.scrollTop)
      }
    },
    enableNormalScroll () {
      // remove the eventhandler by assigning an empty function
      window.onscroll = function () { }
    },
    computeSwimlanePath (x, y, width, height) {
      y = y - 0.5
      return `M${x},${y}h${width}`
    },
    getActionsMenuXPosition (node) {
      // const width = 349 // 341px + 8px padding
      // const minX = this.bpmnScrollLeft || 0
      // let maxX = Math.max(minX + this.diagramViewWidth - width, node.x - width)
      // if (node.x < maxX) {
      //   maxX = node.x + 104
      // }
      // return Math.min(Math.max(node.x - 117, 38 + minX), maxX)
      const minX = 33
      const value = node.x - 117
      return Math.max(minX, value)
    },
    constrainActionsMenuYPosition (node) {
      let offset = 105
      if (this.hasGroups) {
        offset = 129
      }
      return node.y + offset
    },
    closeTransformOptions () {
      return { handler: this.hideTransformOptions, include: this.getBodyIfSubworkflowDialogIsOpen }
    },
    getBodyIfSubworkflowDialogIsOpen () {
      if (document.querySelector('.selectSubworkflowDialog')) {
        return [document.querySelector('body')]
      } else {
        return []
      }
    },
    hideTransformOptions () {
      this.showTransformOptions = false
    },
    handleScroll (ev) {
      const scrollLeft = this.$refs.scrollContainer.scrollLeft
      this.setBpmnScrollLeft(scrollLeft)
    },
    handleWindowResize () {
      const svgContainerWidth = window.innerWidth - 40
      this.setSvgContainerWidth(svgContainerWidth)
    },
    shouldSwitchWithChild (newSlotX, oldSlotX, { childNode }, preserveOrder) {
      return (
        childNode &&
        newSlotX > oldSlotX &&
        positionHelpers.getSlotXFromId(childNode.id, this.svgGraph) === newSlotX &&
        !preserveOrder
      )
    },
    shouldSwitchWithParent (newSlotX, oldSlotX, { parentNode }, preserveOrder) {
      return (
        parentNode &&
        newSlotX < oldSlotX &&
        newSlotX === positionHelpers.getSlotXFromId(parentNode.id, this.svgGraph) &&
        !preserveOrder
      )
    },
    // set the attribute of a DOM element
    // for testing purposes
    setDOMAttribute (id, attribute, value) {
      document.getElementById(id).setAttribute(attribute, value)
    },
    markOccupied (xSlot, ySlotStart, ySlotEnd) {
      const loopStart = Math.min(ySlotStart, ySlotEnd)
      const loopEnd = Math.max(ySlotStart, ySlotEnd)
      for (let y = loopStart; y <= loopEnd; y++) {
        const key = `${xSlot},${y}`
        this.occupiedSlotsVerticalLines[key] = true
      }
    },
    isColliding (xSlot, ySlotStart, ySlotEnd, backwardsConnection) {
      let occupiedCounter = 0
      const loopStart = Math.min(ySlotStart, ySlotEnd)
      const loopEnd = Math.max(ySlotStart, ySlotEnd)
      for (let y = loopStart; y <= loopEnd; y++) {
        const key = `${xSlot},${y}`
        if (this.occupiedSlotsVerticalLines?.[key]) {
          occupiedCounter++
          if (occupiedCounter > 1 || (backwardsConnection && occupiedCounter > 0)) {
            return true
          }
        }
      }
      return false
    }
  },
  mounted () {
    this.latestEventId = this.$route.params.eventId
    this.handleWindowResize()
    window.addEventListener('resize', this.handleWindowResize)
    document.addEventListener('mouseup', this.endDrag)
    this.$refs.scrollContainer.addEventListener('scroll', this.handleScroll)
    const svg = document.getElementById('flowchart')
    if (svg) {
      svg.addEventListener('mousedown', this.startDrag)
      svg.addEventListener('mousemove', this.drag)
      svg.addEventListener('mouseup', this.endDrag)
    }
    EventBus.$on('enable-connection-targets', val => {
      this.enableConnectionTargets = val
      if (val) {
        if (!this.onEscPressed) {
          this.onEscPressed = event => {
            EventBus.$emit('enable-connection-targets', false)
          }
          window.addEventListener('keyup', this.onEscPressed)
        }
      } else {
        if (this.onEscPressed) {
          window.removeEventListener('keyup', this.onEscPressed)
          delete this.onEscPressed
        }
      }
    })
    EventBus.$on('reset-diagram', () => {
      this.resetDiagram()
    })
    EventBus.$on('open-card-comments', item => {
      if (item.eventId) {
        this.inspectEvent(item.eventId, 'comments')
      }
    })
    EventBus.$on('update-data-model', async item => {
      // currently we need up update svgGraph to update read models & commands
      // this should probably be refactored in the future
      // if we create separate mutations for data model it will be a lot of extra code
      // and we need to solve simultaneous updates from the same user (click and blur at the same time)
      if (this.loadingWorkflowUpdate) {
        return
      }

      const { indexDataModel, indexDataField, fieldName, indexAttribute = -1, value } = item
      if (indexAttribute >= 0) {
        console.log('update of first kind')
        this.svgGraph[item.eventId].dataModels[indexDataModel].dataFields[indexDataField][fieldName][indexAttribute] = value
      } else {
        console.log('update of second kind')
        this.svgGraph[item.eventId].dataModels[indexDataModel].dataFields[indexDataField][fieldName] = value
      }
      if (item?.relatedTargetCapturesDescription) {
        return
      }
      await this.$nextTick() // wait for possible other blur event to complete
      workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'updated data model',
          target: this.svgGraph[item.eventId].name
        }
      }, true)
    })
    EventBus.$on('update-data-model-with-function', async ({ eventId, updateFunction, skipFocus }) => {
      await this.$nextTick() // wait for possible other blur event to complete

      // Find the requirement card which has the `eventId`
      const eventElement = document.querySelector(`[data-event-id="${eventId}"]`)
      const beforeFieldsCount = eventElement && eventElement.querySelectorAll('.quick-rename-input').length

      updateFunction(this.svgGraph[eventId])
      await workflowApi.updateWorkflow({
        svgGraph: this.svgGraph,
        svgLanes: this.svgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'updated data model',
          target: this.svgGraph[eventId].name
        }
      }, true)
      await this.$nextTick()

      const afterFieldsCount = eventElement && eventElement.querySelectorAll('.quick-rename-input').length

      EventBus.$emit('refresh-entity-relationship-diagram')

      await this.$nextTick()

      if (!skipFocus && eventElement && beforeFieldsCount !== afterFieldsCount) {
        // Find the last element with the 'quick-rename-input' class and focus it
        const lastCommandField = [...eventElement.querySelectorAll('.quick-rename-input')].pop()
        if (lastCommandField) {
          lastCommandField.focus()
        }
      }
    })
    this.svgLanes = Object.assign({}, this.svgLanesData)
    this.resetDiagram()

    this.cardsByEventId = this.computeCardsByEventId()
  },

  beforeUnmount () {
    EventBus.$off('enable-connection-targets')
    EventBus.$off('reset-diagram')
    EventBus.$off('open-card-comments')
    EventBus.$off('update-data-model')
    EventBus.$off('update-data-model-with-function')
    window.removeEventListener('resize', this.handleWindowResize)
    document.removeEventListener('mouseup', this.endDrag)
    this.$refs.scrollContainer?.removeEventListener('scroll', this.handleScroll)
    const svg = document.getElementById('flowchart')
    if (svg) {
      svg.removeEventListener('mousedown', this.startDrag)
      svg.removeEventListener('mousemove', this.drag)
      svg.removeEventListener('mouseup', this.endDrag)
    }
    if (this.onEscPressed) {
      window.removeEventListener('keyup', this.onEscPressed)
    }
  },

  watch: {
    svgGraphData: {
      handler: function (newVal, oldVal) {
        this.occupiedSlotsVerticalLines = {}
        this.resetDiagram()
      },
      deep: true
    },
    svgLanesData: function (newLanesData, oldLanesData) {
      this.svgLanes = Object.assign({}, this.svgLanesData)
    },
    slotX: function (newSlotX, oldSlotX) {
      if (!this.watchInPause && this.svgGraph[this.selectedElement?.id]) {
        document.querySelectorAll(`[id='${this.selectedElement.id}']`).forEach(el => {
          el.setAttribute('data-test-slot', newSlotX)
        })
        const involvedNodes = dragFunctions.getSurroundingNodes(this.selectedElement.id, this.svgGraph, this.svgGroups)
        let { switchedPlaces, preserveOrder } = dragFunctions.handleGroupSwitch(involvedNodes, newSlotX, oldSlotX, this.slotToGroupMap, this.svgGraph, this.svgGroups)
        if (this.shouldSwitchWithChild(newSlotX, oldSlotX, involvedNodes, preserveOrder)) {
          dragFunctions.switchCurrentNodeWithChild(involvedNodes, this.svgGraph, this.svgGroups) // dragging right
          switchedPlaces = true
        } else if (this.shouldSwitchWithParent(newSlotX, oldSlotX, involvedNodes, preserveOrder)) {
          dragFunctions.switchCurrentNodeWithParent(involvedNodes, this.svgGraph, this.svgGroups) // dragging left
          switchedPlaces = true
        }
        if (switchedPlaces) {
          workflowApiHelpers.refreshDiagramPartially(this.svgGraph, this.svgLanes, this.svgGroups, involvedNodes, this.selectedElement)
        }
      }
    },
    dragging (newVal) {
      this.showActions = !newVal && this.$route.params.eventId
    },
    disabled (newVal) {
      if (newVal) {
        const svg = document.getElementById('flowchart')
        svg.removeEventListener('mousedown', this.startDrag)
        svg.removeEventListener('mousemove', this.drag)
        svg.removeEventListener('mouseup', this.endDrag)
      } else {
        const svg = document.getElementById('flowchart')
        svg.addEventListener('mousedown', this.startDrag)
        svg.addEventListener('mousemove', this.drag)
        svg.addEventListener('mouseup', this.endDrag)
      }
    },
    $route: {
      handler: function () {
        this.occupiedSlotsVerticalLines = {}
        this.showActions = !this.dragging && this.$route.params.eventId
        this.showTransformOptions = false
        if (this.$route.params.eventId) {
          this.latestEventId = this.$route.params.eventId
          if (!this.hasClosedSidePanel) {
            // Only open until user has manually closed it once
            this.$store.commit('showSidePanel', true)
          }
        } else {
          this.latestEventId = null
          if (!this.hasClosedSidePanel && ['cards', 'dataFields', 'fileUpload'].includes(this.sidePanelTab)) {
            this.$store.commit('showSidePanel', false)
          }
        }
      },
      deep: true
    },
    bpmnScrollLeft (newVal) {
      // receive scroll event from the UserStoryMapping component
      this.$refs.scrollContainer.scrollLeft = newVal
    },
    events: {
      handler: function (newVal) {
        this.cardsByEventId = this.computeCardsByEventId()
      },
      deep: true
    }
  }
}

</script>

<style scoped>
svg {
  fill: #ffffff;
}

.ml-05 {
  margin-left: 2px;
}

.canvas-container {
  min-height: 140px;
}

.scroll-container {
  position: relative;
  overflow-x: scroll;
  scrollbar-width: none;
  min-height: 140px;
}

.scrollable-container::-webkit-scrollbar {
  display: none;
}

.scroll-container::-webkit-scrollbar-thumb {
  height: 8px;
  border: 1px solid var(--background);
  border-radius: 5px;
  background-color: #aaa;
  background-origin: content-box;
}

.group {
  background: #CFD3D9;
  height: 24px;
  padding: 4px 8px 3px 8px;
  text-align: center;
}

.group-first {
  border-top-left-radius: 4px;
}

.group-last {
  border-top-right-radius: 4px;
}

.show-side-panel-button {
  position: fixed;
  top: 22px;
  right: -2px;
  z-index: 9;
}

.actions-enter-active,
.actions-leave-active {
  transition: opacity 0.15s ease;
}

.actions-enter-from,
.actions-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.2s ease-in-out;
}

.slide-in-enter-from {
  transform: translateX(370px);
}

.slide-in-enter-to {
  transform: translateX(0);
}

.slide-in-leave-from {
  transform: translateX(0);
}

.slide-in-leave-to {
  transform: translateX(370px);
}

.v-tooltip__content {
  background: white;
  color: var(--color-primary);
  border-radius: 10px;
}

.v-tooltip__content code {
  background: var(--color-primary);
  color: white;
}

.requirement-badge {
  cursor: pointer;
  width: 15px;
  height: 15px;
  text-align: center;
  line-height: 16px;
  border-radius: 2px !important;
  flex-shrink: 0;
}

.requirement-more-badge {
  cursor: pointer;
  text-align: center;
  border-radius: 2px !important;
  background-color: #363A40;
  color: #FCFCFC;
  flex-grow: 1;
  margin-left: 2px;
}

.requirement-badge-number {
  display: block;
  width: 100%;
  height: 100%;
  font-size: 12px;
}

.requirement-section-header {
  display: flex;
  justify-content: space-between;
}

.requirement-section-header > *:first-child {
  flex-grow: 1;
  margin-right: 4px;
}

.requirement-section-header > * {
  padding: 2px 8px;
  border-radius: 6px;
}

.requirement-description {
  border-bottom: 1px solid #e3e3e3;
}

.bpmn-event-container:focus-within + .subprocess-icon {
  display: none;
}

.delete-role-button {
  position: absolute;
  left: 35px;
  top: 10px;
  display: none;
  cursor: pointer;
}

.lane-title-container {
  width: 100%;
  height: 100%;
}

.v-avatar {
  border-radius: 30% !important;
  position: static;
}

.btn-add-event-to {
  border-radius: 50% !important;
}
</style>

<style>
[contenteditable] {
  -webkit-user-select: text;
  user-select: text;
}

.diagram-container {
  margin-right: -40px;
  margin-bottom: 0px;
  overflow: visible;
  min-height: 176px;
}

.sequence-arrow {
  fill: none;
  stroke: #1F2833;
  stroke-linejoin: round;
  stroke-linecap: round;
  marker-end: url('#sequenceflow-end-white-black-1r79xjtcycpwpzz6dais2xasb');
}

.outlined {
  stroke-width: 0.5px !important;
}

path.highlighted {
  filter: drop-shadow(1px 1px 0px black);
}

#flowchart path {
  stroke: #1F2833;
  stroke-width: 1px;
  fill-opacity: 1;
}

#flowchart {
  display: block;
}

.swim-lane {
  stroke: black;
  stroke-width: 2px;
  fill-opacity: 0;
}

/* .swim-lane:hover ~ .move-lane-down-button{
  display: block;
} */

.separator {
  height: 60px;
}

.create-first-event-button {
  padding-left: 10px;
  padding-right: 10px;
  text-transform: none !important;
}

.draggable {
  cursor: move !important;
}

.draggable.horizontal {
  cursor: ew-resize !important;
  user-select: none;
}

.notDraggable {
  cursor: default !important;
}

rect:hover,
text:hover {
  cursor: pointer;
}

.diagram-actions {
  width: 100%;
  height: 36px;
  border-top: 1px solid #e3e3e3;
}

svg .position-fixed {
  position: fixed;
}

svg a {
  position: fixed;
}

.cursor-grab {
  cursor: grab !important;
}

.cursor-grab:active {
  cursor: grabbing !important;
}

</style>
